import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowLeft, MapPin, Ruler, TrendingUp, BedDouble, Bath, FileText, Download, Calendar as CalendarIcon, ArrowRight, Heart, Share2, X, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import MaskedLines from "../components/MaskedLines";
import Reveal from "../components/Reveal";
import api from "../lib/api";
import { useFavourites } from "../lib/favourites";
import { Calendar } from "../components/ui/calendar";

const CURR_LABEL = { INR: "₹", USD: "$", AED: "AED " };
function fmtPrice(inr, curr = "INR") {
  const fx = curr === "USD" ? 1 / 84 : curr === "AED" ? 1 / 22.9 : 1;
  const v = inr * fx;
  if (v >= 10000000 && curr === "INR") return `${CURR_LABEL[curr]}${(v / 10000000).toFixed(2)} Cr`;
  if (v >= 1000000) return `${CURR_LABEL[curr]}${(v / 1000000).toFixed(2)}M`;
  return `${CURR_LABEL[curr]}${v.toFixed(0)}`;
}

const TAG_COLOR = {
  "Verified Clear-Title": "text-[#3E5B41] border-[#3E5B41]",
  "High Yield 9%": "text-[#D4AF37] border-[#D4AF37]",
  "Bank Auction Flip": "text-[#7A1C1C] border-[#7A1C1C]",
  "45% Below Market": "text-[#7A1C1C] border-[#7A1C1C]",
  "JV Ready": "text-[#D4AF37] border-[#D4AF37]",
  "Pre-Leased": "text-[#3E5B41] border-[#3E5B41]",
};

export default function PropertyDetail({ currency }) {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [book, setBook] = useState({ name: "", email: "", phone: "", preferred_date: null, preferred_time: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [emi, setEmi] = useState({ down: 20, rate: 8.5, tenure: 20 });
  const [lightbox, setLightbox] = useState({ open: false, idx: 0 });
  const { toggleFav, isFav } = useFavourites();
  const heroRef = useRef(null);

  const shareProperty = async () => {
    if (!p) return;
    const url = `${window.location.origin}/properties/${p.id}`;
    const data = { title: `${p.title} — BBE`, text: `${p.title} in ${p.location}. Check it out!`, url };
    if (navigator.share) {
      try { await navigator.share(data); } catch {}
    } else {
      try { await navigator.clipboard.writeText(url); toast.success("Link copied to clipboard"); } catch { toast.error("Failed to copy link"); }
    }
  };
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  useEffect(() => {
    api.get(`/properties/${id}`).then((r) => setP(r.data)).catch(() => setNotFound(true));
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(book.email)) return toast.error("Enter a valid email");
    if (!/^[+0-9 \-()]{7,20}$/.test(book.phone)) return toast.error("Enter a valid phone");
    if (!book.preferred_date) return toast.error("Pick a preferred date");
    if (!book.preferred_time) return toast.error("Pick a time slot");
    setSubmitting(true);
    const payload = {
      ...book,
      preferred_date: book.preferred_date instanceof Date ? book.preferred_date.toISOString().split("T")[0] : book.preferred_date,
    };
    try {
      await api.post(`/properties/${id}/book-viewing`, payload);
      toast.success("Viewing request received. A partner will confirm within 24 hours.");
      setBook({ name: "", email: "", phone: "", preferred_date: null, preferred_time: "", notes: "" });
    } catch { toast.error("Failed to submit"); }
    finally { setSubmitting(false); }
  };

  if (notFound) return (
    <div className="pt-[172px] max-w-[1440px] mx-auto px-6 md:px-10 min-h-[70vh]">
      <div className="eyebrow">404 · Property</div>
      <h1 className="mt-4 font-serif text-[54px] leading-tight">This dossier isn't available.</h1>
      <Link to="/properties" className="mt-8 inline-block btn-outline"><ArrowLeft size={14} /> Back to portfolio</Link>
    </div>
  );
  if (!p) return <div className="pt-[172px] max-w-[1440px] mx-auto px-6 md:px-10 min-h-[70vh]"><div className="eyebrow">Loading dossier…</div></div>;

  return (
    <div className="pt-[112px]" data-testid="property-detail">
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden">
        <motion.div className="absolute inset-0 -z-10" style={{ y: heroY }}>
          <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A2342]/30 via-[#0A2342]/40 to-[#0A2342]" />
        </motion.div>

        <div className="max-w-[1440px] mx-auto px-6 md:px-10 pt-8">
          <Link to="/properties" className="inline-flex items-center gap-2 text-[11px] tracking-[2px] uppercase text-[#EDE6D3]/70 hover:text-[#D4AF37]" data-testid="back-link">
            <ArrowLeft size={14} /> Back to portfolio
          </Link>
        </div>

        <div className="max-w-[1440px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16">
          <div className="flex flex-wrap gap-2 mb-8">
            {p.tags.map((t) => (
              <span key={t} className={`text-[10px] tracking-[2px] uppercase px-3 py-1.5 border bg-[#0A2342]/95 ${TAG_COLOR[t] || "text-[#EDE6D3] border-[#EDE6D3]"}`}>{t}</span>
            ))}
          </div>
          <div className="chapter-num">Dossier · {p.id.toUpperCase()}</div>
          <h1 className="mt-4 font-serif text-[44px] md:text-[84px] leading-[0.95] tracking-tight max-w-5xl">
            <MaskedLines lines={[p.title]} />
          </h1>
          <div className="mt-6 flex items-center gap-3 text-[14px] text-[#EDE6D3]/75">
            <MapPin size={15} /> {p.location} · <span className="text-[#EDE6D3]/60">{p.type} · {p.status}</span>
          </div>

          {/* Action Buttons */}
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href={`https://wa.me/919900186556?text=${encodeURIComponent(`Hi BBE, I'm interested in the property "${p.title}" (${p.type}) in ${p.location}, priced at ₹${p.price_inr?.toLocaleString()}. I'd like to know more and possibly schedule a viewing.`)}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-3 bg-[#25D366] text-white px-6 py-3 text-[12px] tracking-[1.5px] uppercase font-medium hover:bg-[#128C7E] transition-colors"
              data-testid="whatsapp-detail"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
              Chat on WhatsApp
            </a>
            <button
              onClick={() => toggleFav(p.id)}
              className={`inline-flex items-center gap-2 px-5 py-3 border text-[12px] tracking-[1.5px] uppercase font-medium transition-colors ${
                isFav(p.id)
                  ? "bg-[#D4AF37] border-[#D4AF37] text-[#0A2342]"
                  : "border-[#EDE6D3]/30 text-[#EDE6D3] hover:border-[#D4AF37]"
              }`}
              data-testid="fav-detail-toggle"
            >
              <Heart size={16} className={isFav(p.id) ? "fill-[#0A2342]" : ""} />
              {isFav(p.id) ? "Saved" : "Save to Shortlist"}
            </button>
            <button
              onClick={shareProperty}
              className="inline-flex items-center gap-2 px-5 py-3 border border-[#EDE6D3]/30 text-[#EDE6D3] text-[12px] tracking-[1.5px] uppercase font-medium hover:border-[#D4AF37] transition-colors"
              data-testid="share-detail"
            >
              <Share2 size={16} /> Share
            </button>
          </div>

          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl">
            <Stat label="Price" value={fmtPrice(p.price_inr, currency)} accent />
            <Stat label="Area" value={`${p.area_sqft.toLocaleString()} sqft`} />
            <Stat label={p.type === "Land" ? "GDV Yield" : "Yield"} value={p.yield_pct ? `${p.yield_pct}%` : "—"} />
            <Stat label="Status" value={p.status} />
          </div>
        </div>
      </section>

      {/* OVERVIEW + HIGHLIGHTS */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-24 grid grid-cols-1 md:grid-cols-12 gap-12">
        <Reveal className="md:col-span-7">
          <div className="chapter-num">§ Overview</div>
          <p className="mt-6 font-serif text-[26px] md:text-[32px] leading-[1.35] tracking-tight">{p.description}</p>
        </Reveal>
        <Reveal className="md:col-span-5" delay={0.1}>
          <div className="chapter-num">§ Highlights</div>
          <ul className="mt-6 space-y-4">
            {p.highlights.map((h, i) => (
              <li key={i} className="flex gap-4 border-b border-[#EDE6D3]/10 pb-4">
                <span className="chapter-num min-w-[24px]">— 0{i + 1}</span>
                <span className="text-[14px] leading-[1.7] text-[#EDE6D3]/80">{h}</span>
              </li>
            ))}
          </ul>
          {(p.beds > 0 || p.baths > 0) && (
            <div className="mt-8 flex items-center gap-8 text-[13px] text-[#EDE6D3]/75">
              {p.beds > 0 && <div className="flex items-center gap-2"><BedDouble size={16} color="#D4AF37" /> {p.beds} Beds</div>}
              {p.baths > 0 && <div className="flex items-center gap-2"><Bath size={16} color="#D4AF37" /> {p.baths} Baths</div>}
              <div className="flex items-center gap-2"><Ruler size={16} color="#D4AF37" /> {p.area_sqft.toLocaleString()} sqft</div>
            </div>
          )}
        </Reveal>
      </section>

      {/* GALLERY */}
      {p.gallery?.length > 0 && (
        <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-32" data-testid="gallery">
          <div className="chapter-num mb-6">§ Gallery</div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {p.gallery.map((src, i) => (
              <Reveal key={i} delay={i * 0.06} className={i === 0 ? "md:col-span-8" : "md:col-span-4"}>
                <button
                  onClick={() => setLightbox({ open: true, idx: i })}
                  className="relative overflow-hidden card-sand a-16-10 w-full cursor-zoom-in"
                  data-testid={`gallery-img-${i}`}
                >
                  <img src={src} alt={`gallery ${i}`} loading="lazy" className="w-full h-full object-cover transition-transform duration-[900ms] hover:scale-105" />
                </button>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* LIGHTBOX */}
      <AnimatePresence>
        {lightbox.open && p.gallery?.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] bg-[#0A2342]/95 backdrop-blur-md flex items-center justify-center"
            onClick={() => setLightbox({ ...lightbox, open: false })}
            onKeyDown={(e) => {
              if (e.key === "Escape") setLightbox({ ...lightbox, open: false });
              if (e.key === "ArrowRight") setLightbox((l) => ({ ...l, idx: (l.idx + 1) % p.gallery.length }));
              if (e.key === "ArrowLeft") setLightbox((l) => ({ ...l, idx: (l.idx - 1 + p.gallery.length) % p.gallery.length }));
            }}
            tabIndex={0}
            data-testid="lightbox"
          >
            {/* Close */}
            <button
              onClick={() => setLightbox({ ...lightbox, open: false })}
              className="absolute top-6 right-6 z-10 p-3 border border-[#EDE6D3]/30 hover:border-[#D4AF37] hover:bg-[#D4AF37] transition-colors group"
              data-testid="lightbox-close"
            >
              <X size={20} className="group-hover:text-[#0A2342]" />
            </button>

            {/* Counter */}
            <div className="absolute top-6 left-6 text-[11px] tracking-[2px] uppercase text-[#EDE6D3]/60">
              {lightbox.idx + 1} / {p.gallery.length}
            </div>

            {/* Prev */}
            <button
              onClick={(e) => { e.stopPropagation(); setLightbox((l) => ({ ...l, idx: (l.idx - 1 + p.gallery.length) % p.gallery.length })); }}
              className="absolute left-4 md:left-8 z-10 p-3 border border-[#EDE6D3]/25 hover:border-[#D4AF37] hover:bg-[#D4AF37] transition-colors group"
              data-testid="lightbox-prev"
            >
              <ChevronLeft size={24} className="group-hover:text-[#0A2342]" />
            </button>

            {/* Image */}
            <motion.div
              key={lightbox.idx}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.92 }}
              transition={{ duration: 0.3 }}
              className="max-w-[85vw] max-h-[80vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={p.gallery[lightbox.idx]}
                alt={`Gallery ${lightbox.idx + 1}`}
                className="max-w-full max-h-[80vh] object-contain"
                data-testid="lightbox-image"
              />
            </motion.div>

            {/* Next */}
            <button
              onClick={(e) => { e.stopPropagation(); setLightbox((l) => ({ ...l, idx: (l.idx + 1) % p.gallery.length })); }}
              className="absolute right-4 md:right-8 z-10 p-3 border border-[#EDE6D3]/25 hover:border-[#D4AF37] hover:bg-[#D4AF37] transition-colors group"
              data-testid="lightbox-next"
            >
              <ChevronRight size={24} className="group-hover:text-[#0A2342]" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* FLOOR PLAN + DOCS */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-32 grid grid-cols-1 md:grid-cols-12 gap-10">
        <Reveal className="md:col-span-7" data-testid="floor-plan">
          <div className="chapter-num mb-6">§ Floor Plan</div>
          <div className="card-sand a-16-10 overflow-hidden">
            <img src={p.floor_plan} alt="Floor plan" className="w-full h-full object-cover" />
          </div>
          <p className="mt-4 text-[12px] text-[#EDE6D3]/60 tracking-[1px] uppercase">Indicative rendering · true-to-scale drawings issued on NDA</p>
        </Reveal>
        <Reveal className="md:col-span-5" delay={0.1} data-testid="document-tray">
          <div className="chapter-num mb-6">§ Document Tray</div>
          <ul className="border-t border-[#EDE6D3]/15">
            {p.documents.map((d, i) => (
              <a
                key={i}
                href={`${process.env.REACT_APP_BACKEND_URL}/api/properties/${p.id}/docs/${i}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between gap-4 py-5 border-b border-[#EDE6D3]/10 group cursor-pointer"
                data-testid={`doc-${i}`}
              >
                <div className="flex items-start gap-4">
                  <div className="border border-[#D4AF37] p-2 mt-1"><FileText size={16} color="#D4AF37" /></div>
                  <div>
                    <div className="font-serif text-[17px] leading-tight group-hover:text-[#D4AF37] transition-colors">{d.name}</div>
                    <div className="text-[11px] tracking-[2px] uppercase text-[#EDE6D3]/50 mt-1">{d.type}</div>
                  </div>
                </div>
                <div className="border border-[#EDE6D3]/20 p-2 group-hover:border-[#D4AF37] group-hover:bg-[#D4AF37] transition-colors">
                  <Download size={15} />
                </div>
              </a>
            ))}
          </ul>
          <p className="mt-4 text-[11px] text-[#EDE6D3]/50 tracking-[1px] uppercase">Sample dossier · full unredacted document tray unlocks post NDA via the Investor Desk.</p>

          {/* Brochure Download */}
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/properties/${p.id}/brochure`}
            target="_blank"
            rel="noreferrer"
            className="mt-8 flex items-center gap-3 p-5 border border-[#D4AF37]/40 bg-[#D4AF37]/5 hover:bg-[#D4AF37]/15 transition-colors group"
            data-testid="brochure-download"
          >
            <div className="bg-[#D4AF37] p-2.5"><Download size={18} color="#0A2342" /></div>
            <div className="flex-1">
              <div className="font-serif text-[17px] leading-tight text-[#D4AF37]">Download Brochure</div>
              <div className="text-[11px] tracking-[1.5px] uppercase text-[#EDE6D3]/50 mt-1">Branded BBE PDF · Print-ready</div>
            </div>
            <ArrowRight size={16} className="text-[#D4AF37] group-hover:translate-x-1 transition-transform" />
          </a>
        </Reveal>
      </section>

      {/* EMI CALCULATOR */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-32" data-testid="emi-calculator">
        <Reveal>
          <div className="card-sand p-8 md:p-14">
            <div className="chapter-num mb-4">§ EMI Calculator</div>
            <h2 className="font-serif text-[32px] md:text-[44px] leading-[1.05] tracking-tight">Plan your financing.</h2>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-10">
              <div className="md:col-span-5 space-y-8">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="bb-label">Down Payment</div>
                    <div className="text-[14px] font-serif text-[#D4AF37]">{emi.down}%</div>
                  </div>
                  <input
                    type="range" min="5" max="80" step="5"
                    value={emi.down}
                    onChange={(e) => setEmi({ ...emi, down: +e.target.value })}
                    className="w-full accent-[#D4AF37]"
                    data-testid="emi-down"
                  />
                  <div className="flex justify-between text-[10px] text-[#EDE6D3]/40 mt-1"><span>5%</span><span>80%</span></div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="bb-label">Interest Rate</div>
                    <div className="text-[14px] font-serif text-[#D4AF37]">{emi.rate}%</div>
                  </div>
                  <input
                    type="range" min="6" max="14" step="0.25"
                    value={emi.rate}
                    onChange={(e) => setEmi({ ...emi, rate: +e.target.value })}
                    className="w-full accent-[#D4AF37]"
                    data-testid="emi-rate"
                  />
                  <div className="flex justify-between text-[10px] text-[#EDE6D3]/40 mt-1"><span>6%</span><span>14%</span></div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="bb-label">Tenure</div>
                    <div className="text-[14px] font-serif text-[#D4AF37]">{emi.tenure} years</div>
                  </div>
                  <input
                    type="range" min="5" max="30" step="1"
                    value={emi.tenure}
                    onChange={(e) => setEmi({ ...emi, tenure: +e.target.value })}
                    className="w-full accent-[#D4AF37]"
                    data-testid="emi-tenure"
                  />
                  <div className="flex justify-between text-[10px] text-[#EDE6D3]/40 mt-1"><span>5 yr</span><span>30 yr</span></div>
                </div>
              </div>

              <div className="md:col-span-7 md:col-start-7">
                {(() => {
                  const price = p.price_inr;
                  const loan = price * (1 - emi.down / 100);
                  const r = emi.rate / 100 / 12;
                  const n = emi.tenure * 12;
                  const monthly = r > 0 ? (loan * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1) : loan / n;
                  const totalPayable = monthly * n;
                  const totalInterest = totalPayable - loan;

                  const fmt = (v) => {
                    if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
                    if (v >= 100000) return `₹${(v / 100000).toFixed(2)} L`;
                    return `₹${Math.round(v).toLocaleString()}`;
                  };

                  return (
                    <div className="space-y-6">
                      <div className="border border-[#D4AF37]/30 p-8" data-testid="emi-result">
                        <div className="text-[11px] tracking-[2px] uppercase text-[#EDE6D3]/50 mb-2">Monthly EMI</div>
                        <div className="font-serif text-[48px] md:text-[64px] leading-none tracking-tight text-[#D4AF37]" data-testid="emi-monthly">
                          {fmt(monthly)}
                        </div>
                        <div className="text-[12px] text-[#EDE6D3]/50 mt-2">per month for {emi.tenure} years</div>
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div className="border-t border-[#EDE6D3]/15 pt-4">
                          <div className="text-[10px] tracking-[2px] uppercase text-[#EDE6D3]/40">Loan Amount</div>
                          <div className="font-serif text-[20px] mt-1" data-testid="emi-loan">{fmt(loan)}</div>
                        </div>
                        <div className="border-t border-[#EDE6D3]/15 pt-4">
                          <div className="text-[10px] tracking-[2px] uppercase text-[#EDE6D3]/40">Total Interest</div>
                          <div className="font-serif text-[20px] mt-1" data-testid="emi-interest">{fmt(totalInterest)}</div>
                        </div>
                        <div className="border-t border-[#EDE6D3]/15 pt-4">
                          <div className="text-[10px] tracking-[2px] uppercase text-[#EDE6D3]/40">Down Payment</div>
                          <div className="font-serif text-[20px] mt-1" data-testid="emi-downamt">{fmt(price * emi.down / 100)}</div>
                        </div>
                      </div>

                      <div className="text-[11px] text-[#EDE6D3]/40 leading-[1.6]">
                        Indicative calculation only. Actual EMI depends on bank-specific rates, processing fees, and credit profile. Rates effective as of current RBI repo rate cycle.
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* BOOK A VIEWING */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 pt-32">
        <Reveal>
          <div className="bg-[#EDE6D3] text-[#0A2342] p-8 md:p-16 grid grid-cols-1 md:grid-cols-12 gap-10 relative grain overflow-hidden" data-testid="book-viewing">
            <div className="md:col-span-5">
              <div className="chapter-num text-[#D4AF37]">§ Site Visit</div>
              <h2 className="mt-6 font-serif text-[36px] md:text-[56px] leading-[0.95] tracking-tight">Book a viewing.</h2>
              <p className="mt-6 text-[14px] leading-[1.8] text-[#0A2342]/70 max-w-md">Chauffeured visits from anywhere in Bengaluru. Bring your own architect, valuer or legal counsel — every question welcomed.</p>
              <div className="mt-8 flex items-center gap-3 text-[13px] text-[#0A2342]/80">
                <CalendarIcon size={15} color="#D4AF37" /> <span>Slots typically confirmed within 24 hours</span>
              </div>

              {/* Visual Calendar */}
              <div className="mt-8" data-testid="viewing-calendar">
                <div className="bb-label text-[#0A2342]/60 mb-3">Pick a Date</div>
                <div className="bg-white/60 border border-[#0A2342]/15 p-3 inline-block">
                  <Calendar
                    mode="single"
                    selected={book.preferred_date}
                    onSelect={(d) => setBook({ ...book, preferred_date: d })}
                    disabled={(d) => d < new Date() || d.getDay() === 0}
                    className="text-[#0A2342]"
                  />
                </div>
                {book.preferred_date && (
                  <div className="mt-3 text-[12px] text-[#D4AF37] tracking-[1px] font-medium">
                    Selected: {book.preferred_date.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
                  </div>
                )}
              </div>

              {/* Time Slots */}
              <div className="mt-6" data-testid="time-slots">
                <div className="bb-label text-[#0A2342]/60 mb-3">Pick a Time Slot</div>
                <div className="flex flex-wrap gap-2">
                  {["10:00 AM", "11:30 AM", "1:00 PM", "2:30 PM", "4:00 PM", "5:30 PM"].map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setBook({ ...book, preferred_time: t })}
                      className={`px-4 py-2 text-[11px] tracking-[1.5px] uppercase border transition-colors ${
                        book.preferred_time === t
                          ? "bg-[#D4AF37] border-[#D4AF37] text-[#0A2342] font-bold"
                          : "border-[#0A2342]/25 text-[#0A2342]/70 hover:border-[#D4AF37]"
                      }`}
                      data-testid={`time-${t.replace(/[: ]/g, "")}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <form onSubmit={submit} className="md:col-span-7 flex flex-col gap-6" data-testid="viewing-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                <div><div className="bb-label text-[#0A2342]/60">Name</div><input required className="bb-input text-[#0A2342] border-b-[#0A2342]/30" value={book.name} onChange={(e) => setBook({ ...book, name: e.target.value })} data-testid="view-name" /></div>
                <div><div className="bb-label text-[#0A2342]/60">Email</div><input required type="email" className="bb-input text-[#0A2342] border-b-[#0A2342]/30" value={book.email} onChange={(e) => setBook({ ...book, email: e.target.value })} data-testid="view-email" /></div>
                <div><div className="bb-label text-[#0A2342]/60">Phone</div><input required className="bb-input text-[#0A2342] border-b-[#0A2342]/30" value={book.phone} onChange={(e) => setBook({ ...book, phone: e.target.value })} data-testid="view-phone" /></div>
                <div>
                  <div className="bb-label text-[#0A2342]/60">Selected Slot</div>
                  <div className="bb-input text-[#0A2342] border-b-[#0A2342]/30 text-[14px]">
                    {book.preferred_date && book.preferred_time
                      ? `${book.preferred_date.toLocaleDateString("en-IN", { day: "numeric", month: "short" })} · ${book.preferred_time}`
                      : <span className="text-[#0A2342]/40">Pick date & time ←</span>
                    }
                  </div>
                </div>
              </div>
              <div><div className="bb-label text-[#0A2342]/60">Notes</div><textarea rows={3} className="bb-input text-[#0A2342] border-b-[#0A2342]/30" value={book.notes} onChange={(e) => setBook({ ...book, notes: e.target.value })} data-testid="view-notes" /></div>
              <button type="submit" disabled={submitting} className="btn-bronze justify-center disabled:opacity-50" data-testid="view-submit">{submitting ? "Submitting…" : "Request Viewing"} <ArrowRight size={14} /></button>
            </form>
          </div>
        </Reveal>
      </section>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="border-t border-[#EDE6D3]/25 pt-4">
      <div className="eyebrow">{label}</div>
      <div className={`mt-3 font-serif text-[28px] md:text-[34px] leading-none tracking-tight ${accent ? "text-[#D4AF37]" : ""}`}>{value}</div>
    </div>
  );
}
